import { useEffect, useRef, useState } from "react";
import { ChevronDown, CheckIcon } from "./ui";
import { addOutsidePress, removeOutsidePress } from "../outsidePress";

/**
 * Compact premium dropdown for picking the course/group scope. Shared by the
 * New-message picker (InboxPane) and the inbox landing shortcut (DiscoverPane).
 * `options`: [{ key, name, member_count? }]. Calls onChange(key).
 */
export default function ScopeSelect({ value, onChange, options, placeholder, primary }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    addOutsidePress(onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      removeOutsidePress(onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = options.find((o) => o.key === value);
  const active = !!value;

  return (
    <div className="relative min-w-0 flex-1" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 rounded-xl border bg-surface px-3 py-2 text-[12.5px] font-semibold shadow-sm transition"
        style={{
          borderColor: active ? primary : open ? `${primary}80` : "rgb(var(--c-slate-200))",
          color: active ? primary : "rgb(var(--c-slate-600))",
          boxShadow: open ? `0 0 0 3px ${primary}1f` : undefined,
        }}
      >
        <span className="min-w-0 flex-1 truncate text-left">{current ? current.name : placeholder}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: active ? primary : "rgb(var(--c-slate-400))" }} />
      </button>
      {open && (
        <div className="chat-pop absolute left-0 right-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-xl border border-slate-100 bg-surface p-1 shadow-[0_16px_48px_-12px_rgba(15,23,42,0.35)]">
          <div className="thin-scroll max-h-56 overflow-y-auto">
            {options.length === 0 ? (
              <p className="px-3 py-3 text-center text-[12px] text-slate-400">Nothing to show yet</p>
            ) : (
              options.map((o) => {
                const on = o.key === value;
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => {
                      onChange(o.key);
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[12.5px] transition hover:bg-slate-50"
                    style={on ? { backgroundColor: `${primary}12`, color: primary, fontWeight: 700 } : { color: "rgb(var(--c-slate-700))" }}
                  >
                    <span className="min-w-0 flex-1 truncate">{o.name}</span>
                    {typeof o.member_count === "number" && (
                      <span className="shrink-0 text-[10px] font-semibold text-slate-400">{o.member_count}</span>
                    )}
                    {on && <CheckIcon className="h-3.5 w-3.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
